import * as THREE from "three/webgpu";
import {Subject, Subscription, take} from 'rxjs';
import {
    EntitySelectionManager,
    type EntityBoxSelectedEventPayload,
    type EntityHoverEnterEventPayload,
    type EntityHoverLeaveEventPayload,
    EntityInteractionEventType,
    type EntitySelectedEventPayload,
} from './EntitySelectionManager';
import {SketchGeometryRelationLabelManager} from './SketchGeometryRelationLabelManager';
import type {Viewer} from '../viewer/Viewer';
import type {FeatureResultMesh} from '../mesh/feature/FeatureResultMesh';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../mesh/sketch';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    type InteractionModeContext,
    type ManualStateHintRecord,
    type InteractionVisualStateContext,
    type InteractionVisualStateRuntime,
    type MaterialSnapshot,
    type FeatureVisualScope,
} from './interactionState/AbstractInteractionVisualState';
import {ViewerMode} from '../viewer/ViewerMode';
import {AssemblyTransitionState} from './interactionState/AssemblyTransitionState';
import {DefaultTransitionState} from './interactionState/DefaultTransitionState';
import {ReferencePlaneTransitionState} from './interactionState/ReferencePlaneTransitionState';
import {ReferenceAxisTransitionState} from './interactionState/ReferenceAxisTransitionState';
import {ReferencePointTransitionState} from './interactionState/ReferencePointTransitionState';
import {AssemblyReferencePlaneTransitionState} from './interactionState/AssemblyReferencePlaneTransitionState';
import {AssemblyReferenceAxisTransitionState} from './interactionState/AssemblyReferenceAxisTransitionState';
import {AssemblyReferencePointTransitionState} from './interactionState/AssemblyReferencePointTransitionState';
import {LinearComponentPatternTransitionState} from './interactionState/LinearComponentPatternTransitionState';
import {SketchDefaultTransitionState} from "./interactionState/SketchDefaultTransitionState";
import {SketchDrawingTransitionState} from "./interactionState/SketchDrawingTransitionState";
import {SketchMoveEntitiesTransitionState} from './interactionState/SketchMoveEntitiesTransitionState';
import {SketchCopyEntitiesTransitionState} from './interactionState/SketchCopyEntitiesTransitionState';
import {SketchRotateEntitiesTransitionState} from './interactionState/SketchRotateEntitiesTransitionState';
import {SketchCircularPatternTransitionState} from './interactionState/SketchCircularPatternTransitionState';
import {SketchTrimTransitionState} from "./interactionState/SketchTrimTransitionState";
import {MeasureSelectionVisualOverlay} from '../measure/annotation/MeasureSelectionVisualOverlay';
import {collectMeasureSelectionVisualObjectIds} from '../measure/selection/MeasureSelectionVisualScope';
import type { SelectionInteractionMeta } from '../imported-model/render/selection-extension';

/**
 * 交互模式变更事件载荷。
 */
export interface InteractionModeChangePayload {
    /** 变更前模式上下文。 */
    previous: InteractionModeContext;
    /** 变更后模式上下文。 */
    current: InteractionModeContext;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 交互模式变更监听器。
 */
export type InteractionModeChangeListener = (payload: InteractionModeChangePayload) => void;

/**
 * 交互状态管理器。
 * 说明：
 * 1. 该类只负责管理事件、模式与状态类切换；
 * 2. 具体的视觉状态修改逻辑全部委托给状态类；
 * 3. 当前默认状态类为“草图默认转状态”。
 * 查看当前选择
 * window.viewer.interactionStateManager.createVisualStateContext();
 */
export class InteractionStateManager {
    /** 渲染器实例。 */
    private readonly renderer: Viewer;
    /** 实体语义事件管理器。 */
    private entitySelectionManager: EntitySelectionManager | null;
    /** 当前模式上下文。 */
    private modeContext: InteractionModeContext = {mode: ViewerMode.Default};

    /** 当前悬浮对象。 */
    private hoveredObject: THREE.Object3D | null = null;
    /** 当前悬浮对象对应的交互语义元信息。 */
    private hoveredInteractionMeta: SelectionInteractionMeta | undefined;
    //------ 线性零部件阵列节点包围盒移入 开始------
    /** 当前程序化多目标移入对象列表。 */
    private programmaticHoveredObjects: THREE.Object3D[] = [];
    /** 当前程序化多目标移入交互元信息。 */
    private programmaticHoveredInteractionMeta: SelectionInteractionMeta | undefined;
    //------ 线性零部件阵列节点包围盒移入 结束------
    //------ 设计树程序化特征移入高亮 开始------
    /** 当前程序化移入命中的特征结果体。 */
    private programmaticHoveredFeatureResultMesh: FeatureResultMesh | null = null;
    /** 当前设计树特征节点 hover 对应的局部视觉范围。 */
    private programmaticHoveredFeatureVisualScope: FeatureVisualScope | null = null;
    /**
     * 当前 hover 特征局部视觉范围的比较键快照。
     *
     * 设计树高亮依赖 FeatureResultMesh 内部拓扑版本；旧 scope 持有同一个 mesh 引用时，
     * 不能在比较时重新读取旧对象，否则会把“拓扑已更新”误判为无变化。
     */
    private programmaticHoveredFeatureVisualScopeKey: string = '';
    //------ 设计树程序化特征移入高亮 结束------
    /** 当前选中对象列表。 */
    private selectedObjects: THREE.Object3D[] = [];
    /** 测量叠加层选中对象列表（与底层命令选中集隔离）。 */
    private measureSelectedObjects: THREE.Object3D[] = [];
    /** 当前主选对象对应的交互语义元信息。 */
    private selectedInteractionMeta: SelectionInteractionMeta | undefined;
    /** 当前设计树特征节点选中对应的局部视觉范围。 */
    private selectedFeatureVisualScope: FeatureVisualScope | null = null;
    /**
     * 当前选中特征局部视觉范围的比较键快照。
     *
     * 该字段与 selectedFeatureVisualScope 同步维护，用于识别同一结果体实例内部
     * topologySnapshot 已更新但 uuid 未变化的情况。
     */
    private selectedFeatureVisualScopeKey: string = '';

    /** 交互模式变化事件流。 */
    private readonly modeChangeSubject: Subject<InteractionModeChangePayload> = new Subject<InteractionModeChangePayload>();
    /** 模式变化监听订阅映射。 */
    private readonly modeChangeSubscriptions: Map<InteractionModeChangeListener, Subscription> = new Map();
    /** 是否已启动。 */
    private isStarted: boolean = false;
    /** 实体语义订阅集合。 */
    private readonly entityEventSubscriptions: Subscription[] = [];
    /** 约束标签状态机。 */
    private readonly relationLabelState: SketchGeometryRelationLabelManager;
    /** 已同步标签的对象 ID 集合。 */
    private readonly syncedMarkerObjectIdSet: Set<string> = new Set();
    /** 交互视觉状态运行时。 */
    private readonly visualStateRuntime: InteractionVisualStateRuntime = {
        objectStateMachine: new Map<string, GeometryInteractionState>(),
        externalStateHints: new Map<string, GeometryInteractionState>(),
        manualStateHints: new Map<string, ManualStateHintRecord>(),
        manualVisibilityHints: new Set<string>(),
        originalMaterialSnapshots: new Map<string, MaterialSnapshot>(),
        stateLineMaterialCache: new Map(),
        stateMeshMaterialCache: new Map(),
        stateControlPointMaterialCache: new Map(),
    };
    /** 当前状态类。 */
    private visualStateHandler: AbstractInteractionVisualState;

    /** 测量选中视觉叠加层。 */
    private readonly measureSelectionOverlay: MeasureSelectionVisualOverlay;

    /**
     * 构造函数。
     * @param value Viewer 实例。
     */
    public constructor(value: Viewer) {
        this.renderer = value;
        this.entitySelectionManager = value.entitySelectionManager;
        this.relationLabelState = new SketchGeometryRelationLabelManager(value);
        this.visualStateHandler = new DefaultTransitionState(this.visualStateRuntime, value);
        this.measureSelectionOverlay = new MeasureSelectionVisualOverlay(this.visualStateRuntime, value);
    }

    /**
     * 启动状态管理。
     */
    public start(): void {
        if (this.isStarted) {
            return;
        }

        if (!this.entitySelectionManager) {
            this.entitySelectionManager = this.renderer.entitySelectionManager;
        }
        if (!this.entitySelectionManager) {
            return;
        }

        this.entityEventSubscriptions.push(
            this.entitySelectionManager.observe(EntityInteractionEventType.EntityHoverEnter).subscribe(this.handleEntityHoverEnter),
            this.entitySelectionManager.observe(EntityInteractionEventType.EntityHoverLeave).subscribe(this.handleEntityHoverLeave),
            this.entitySelectionManager.observe(EntityInteractionEventType.EntitySelected).subscribe(this.handleEntitySelected),
            this.entitySelectionManager.observe(EntityInteractionEventType.EntityBoxSelected).subscribe(this.handleEntityBoxSelected),
        );

        this.isStarted = true;
        this.refreshVisualState();
    }

    /**
     * 停止状态管理。
     */
    public stop(): void {
        this.renderer.cancelVisualUpdate(this);
        if (!this.isStarted) {
            return;
        }

        this.entityEventSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.entityEventSubscriptions.length = 0;

        this.visualStateHandler.resetVisualState();
        this.hoveredObject = null;
        this.hoveredInteractionMeta = undefined;
        //------ 线性零部件阵列节点包围盒移入 开始------
        this.programmaticHoveredObjects = [];
        this.programmaticHoveredInteractionMeta = undefined;
        //------ 线性零部件阵列节点包围盒移入 结束------
        //------ 设计树程序化特征移入高亮 开始------
        this.programmaticHoveredFeatureResultMesh = null;
        this.programmaticHoveredFeatureVisualScope = null;
        this.programmaticHoveredFeatureVisualScopeKey = '';
        //------ 设计树程序化特征移入高亮 结束------
        this.selectedObjects = [];
        this.selectedFeatureVisualScope = null;
        this.selectedFeatureVisualScopeKey = '';
        this.selectedInteractionMeta = undefined;
        this.relationLabelState.clearAllMarkers();
        this.relationLabelState.clearMarkerVisualStates();
        this.syncedMarkerObjectIdSet.clear();

        this.isStarted = false;
    }

    /**
     * 切换视觉状态类。
     * @param handler 新的状态类实例。
     */
    /**
     * 为运行时对象树替换清理交互视觉缓存。
     *
     * 设计说明：
     * 1. undo/redo 会用快照克隆体替换草图子树，旧 Object3D 引用不能继续留在 hover/selection/material 缓存中；
     * 2. 这里只清理交互运行态，不切换 Viewer 模式，避免把模型历史恢复和 UI 模式语义耦合；
     * 3. 恢复完成后由 Viewer 重新触发视觉刷新，使新对象树按当前模式重建默认材质、缩放和拓扑点可见性。
     */
    public prepareForRuntimeObjectTreeReplacement(): void {
        this.renderer.cancelVisualUpdate(this);
        this.visualStateHandler.resetVisualState();
        this.hoveredObject = null;
        this.hoveredInteractionMeta = undefined;
        this.programmaticHoveredObjects = [];
        this.programmaticHoveredInteractionMeta = undefined;
        this.programmaticHoveredFeatureResultMesh = null;
        this.programmaticHoveredFeatureVisualScope = null;
        this.programmaticHoveredFeatureVisualScopeKey = '';
        this.selectedObjects = [];
        this.selectedFeatureVisualScope = null;
        this.selectedFeatureVisualScopeKey = '';
        this.selectedInteractionMeta = undefined;
        this.visualStateRuntime.manualStateHints.clear();
        this.visualStateRuntime.manualVisibilityHints.clear();
        this.relationLabelState.clearMarkerVisualStates();
    }

    /**
     * 切换视觉状态类。
     *
     * @param handler 新的状态类实例。
     */
    public switchVisualStateHandler(handler: AbstractInteractionVisualState): void {
        this.visualStateHandler.resetVisualState();
        this.visualStateHandler.dispose();
        this.visualStateHandler = handler;
        this.refreshVisualState();
    }

    //------ 草图绘制状态切换 开始------
    /**
     * 切换到草图绘制视觉状态。
     * 说明：创建 SketchDrawingTransitionState 实例，替换当前状态类，
     * 绘制模式下不处理轮廓高亮和边选择。
     */
    public switchToDrawingState(): void {
        const drawingState = new SketchDrawingTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(drawingState);
    }

    /**
     * 切换到默认模式视觉状态。
     * 说明：创建 DefaultTransitionState 实例，与默认选择模式保持一致。
     */
    public switchToDefaultState(): void {
        const defaultState = new DefaultTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(defaultState);
    }

    /**
     * 切换到装配体视觉状态。
     * 说明：装配体模式当前复用默认视觉语义，但保留独立状态类承接后续扩展。
     */
    public switchToAssemblyState(): void {
        const assemblyState = new AssemblyTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(assemblyState);
    }

    //------ 线性零部件阵列 开始------
    /**
     * 切换到线性零部件阵列视觉状态。
     *
     * 说明：线性零部件阵列属于装配体命令态，当前复用装配体高亮语义，
     * 但保留独立状态类，避免后续预览、实例球、方向参考高亮与普通装配体选择耦合。
     */
    public switchToLinearComponentPatternState(): void {
        const linearComponentPatternState = new LinearComponentPatternTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(linearComponentPatternState);
    }
    //------ 线性零部件阵列 结束------

    /**
     * 切换到基准面视觉状态。
     * 说明：基准面模式当前沿用默认模式视觉语义，但保留独立状态类承接后续扩展。
     */
    public switchToReferencePlaneState(): void {
        const referencePlaneState = new ReferencePlaneTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(referencePlaneState);
    }

    /**
     * 切换到基准轴视觉状态。
     * 说明：基准轴模式当前沿用默认模式视觉语义，但保留独立状态类承接后续扩展。
     */
    public switchToReferenceAxisState(): void {
        const referenceAxisState = new ReferenceAxisTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(referenceAxisState);
    }

    /**
     * 切换到基准点视觉状态。
     * 说明：基准点模式当前沿用默认模式视觉语义，但保留独立模式分支承接后续扩展。
     */
    public switchToReferencePointState(): void {
        const referencePointState = new ReferencePointTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(referencePointState);
    }

    //------ 装配体模式下基准点线面 开始------
    /**
     * 切换到装配体模式下基准面视觉状态。
     *
     * 说明：使用独立状态类表达装配体基准面语义，当前行为复用普通基准面视觉状态。
     */
    public switchToAssemblyReferencePlaneState(): void {
        const assemblyReferencePlaneState = new AssemblyReferencePlaneTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(assemblyReferencePlaneState);
    }

    /**
     * 切换到装配体模式下基准轴视觉状态。
     *
     * 说明：使用独立状态类表达装配体基准轴语义，当前行为复用普通基准轴视觉状态。
     */
    public switchToAssemblyReferenceAxisState(): void {
        const assemblyReferenceAxisState = new AssemblyReferenceAxisTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(assemblyReferenceAxisState);
    }

    /**
     * 切换到装配体模式下基准点视觉状态。
     *
     * 说明：使用独立状态类表达装配体基准点语义，当前行为复用普通基准点视觉状态。
     */
    public switchToAssemblyReferencePointState(): void {
        const assemblyReferencePointState = new AssemblyReferencePointTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(assemblyReferencePointState);
    }
    //------ 装配体模式下基准点线面 结束------

    /**
     * 切换到草图默认视觉状态。
     * 说明：创建 SketchDefaultTransitionState 实例，恢复草图态完整的悬浮高亮和选择功能。
     */
    public switchToSketchDefaultState(): void {
        const sketchDefaultState = new SketchDefaultTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(sketchDefaultState);
    }

    /**
     * 切换到草图裁剪视觉状态。
     * 说明：裁剪模式下保留 hover 高亮，但不复用默认选择高亮语义。
     */
    public switchToTrimState(): void {
        const trimState = new SketchTrimTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(trimState);
    }

    /**
     * 切换到草图移动实体视觉状态。
     */
    public switchToSketchMoveEntitiesState(): void {
        const moveState = new SketchMoveEntitiesTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(moveState);
    }

    /**
     * 切换到草图复制实体视觉状态。
     */
    public switchToSketchCopyEntitiesState(): void {
        const copyState = new SketchCopyEntitiesTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(copyState);
    }

    /**
     * 切换到草图旋转实体视觉状态。
     */
    public switchToSketchRotateEntitiesState(): void {
        const rotateState = new SketchRotateEntitiesTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(rotateState);
    }

    /**
     * 切换到圆周草图阵列视觉状态。
     */
    public switchToSketchCircularPatternState(): void {
        const patternState = new SketchCircularPatternTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(patternState);
    }

    /** 切换到草图镜向实体视觉状态。 */
    public switchToSketchMirrorEntitiesState(): void {
        const mirrorState = new SketchDefaultTransitionState(this.visualStateRuntime, this.renderer);
        this.switchVisualStateHandler(mirrorState);
    }
    //------ 草图绘制状态切换 结束------

    //------ 模式切换 开始------
    /**
     * 设置测量叠加层选中对象并刷新视觉。
     *
     * @param selectedObjects 测量选中对象列表。
     */
    public setMeasureSelectedObjects(selectedObjects: THREE.Object3D[]): void {
        this.measureSelectedObjects = this.uniqueObjects(selectedObjects);
        // imported 拓扑代理（imported topology proxy）是一类在非原生几何上，为实现测量、显示等功能，由系统自动临时生成的 THREE.Object3D 包装对象。
        // 它们通常在如下场景自动创建：例如从GLB、装配或外部格式导入的几何，无法直接进行高亮/选中/测量时，
        // 系统会解析拓扑结构并为测量等交互需求构建代理对象并自动挂载到场景树内（如包裹BRep面、边、导入零件等）。
        // 创建时机：当用户发生测量选中、hover等需要对这些基础拓扑进行操作时，若原始对象无法直接满足交互需求则即时创建代理。
        // 回收规则：这些代理对象不是永久保留的，当它们既不被选中也不被 hover 或任何功能引用时，为避免内存及场景污染，会在未被引用的情况下被自动移除或回收（如通过 detach/remove）。
        // 因此，需要在 measure selection 变更后，主动同步当前所有选中对象之代理引用，避免单纯因为 hover 离开就误删正在选中的对象，确保测量高亮的持续有效。
        this.entitySelectionManager?.syncImportedTopologyProxyRetention();
        this.refreshVisualState();
    }

    /**
     * 清空测量叠加层选中对象并刷新视觉。
     */
    public clearMeasureSelectedObjects(): void {
        if (this.measureSelectedObjects.length <= 0) {
            this.measureSelectionOverlay.clearSelectedAppearance();
            return;
        }

        this.measureSelectedObjects = [];
        this.measureSelectionOverlay.clearSelectedAppearance();
        this.entitySelectionManager?.syncImportedTopologyProxyRetention();
        this.refreshVisualState();
    }

    /**
     * 设置当前交互模式并切换对应的视觉状态处理器。
     * 说明：
     * 1. 由 Viewer.setMode 直接调用，不再内部猜测模式；
     * 2. 模式变化时主动清除 hover 状态，避免旧模式高亮残留；
     * 3. 按 ViewerMode 直接 switch 切换状态类，不做 instanceof 防御。
     * @param mode 目标模式。
     * @throws 当运行时传入不受支持的交互模式时抛出错误。
     */
    public setMode(mode: ViewerMode): void {
        const previous: InteractionModeContext = {...this.modeContext};
        const isModeChanged = mode !== this.modeContext.mode;
        if (!isModeChanged) {
            return;
        }
        this.modeContext = {mode};

        // 模式切换时主动清除 hover 状态，避免依赖 EntityHoverLeave 事件链清除而产生时序漏洞。
        this.hoveredObject = null;
        this.hoveredInteractionMeta = undefined;
        //------ 线性零部件阵列节点包围盒移入 开始------
        this.programmaticHoveredObjects = [];
        this.programmaticHoveredInteractionMeta = undefined;
        //------ 线性零部件阵列节点包围盒移入 结束------
        //------ 设计树程序化特征移入高亮 开始------
        this.programmaticHoveredFeatureResultMesh = null;
        this.programmaticHoveredFeatureVisualScope = null;
        this.programmaticHoveredFeatureVisualScopeKey = '';
        //------ 设计树程序化特征移入高亮 结束------
        this.selectedInteractionMeta = undefined;
        this.selectedFeatureVisualScope = null;
        this.selectedFeatureVisualScopeKey = '';
        this.visualStateRuntime.manualStateHints.clear();
        this.visualStateRuntime.manualVisibilityHints.clear();
        if (
            previous.mode === ViewerMode.SketchTrim
            || previous.mode === ViewerMode.SketchMoveEntities
            || previous.mode === ViewerMode.SketchCopyEntities
            || previous.mode === ViewerMode.SketchRotateEntities
            || previous.mode === ViewerMode.SketchCircularPattern
            || previous.mode === ViewerMode.SketchMirrorEntities
            || previous.mode === ViewerMode.SketchDimension
            || mode === ViewerMode.SketchTrim
            || mode === ViewerMode.SketchMoveEntities
            || mode === ViewerMode.SketchCopyEntities
            || mode === ViewerMode.SketchRotateEntities
            || mode === ViewerMode.SketchCircularPattern
            || mode === ViewerMode.SketchMirrorEntities
            || mode === ViewerMode.SketchDimension
        ) {
            this.selectedObjects = [];
        }

        // 按模式直接切换对应的视觉状态处理器。
        switch (mode) {
            case ViewerMode.Default:
                this.switchToDefaultState();
                break;
            case ViewerMode.Assembly:
            case ViewerMode.MoveComponent:
                this.switchToAssemblyState();
                break;
            //------ 线性零部件阵列 开始------
            case ViewerMode.LinearComponentPattern:
                this.switchToLinearComponentPatternState();
                break;
            //------ 线性零部件阵列 结束------
            case ViewerMode.ReferencePlane:
                this.switchToReferencePlaneState();
                break;
            case ViewerMode.ReferenceAxis:
                this.switchToReferenceAxisState();
                break;
            case ViewerMode.ReferencePoint:
                this.switchToReferencePointState();
                break;
            //------ 装配体模式下基准点线面 开始------
            case ViewerMode.AssemblyReferencePlane:
                this.switchToAssemblyReferencePlaneState();
                break;
            case ViewerMode.AssemblyReferenceAxis:
                this.switchToAssemblyReferenceAxisState();
                break;
            case ViewerMode.AssemblyReferencePoint:
                this.switchToAssemblyReferencePointState();
                break;
            //------ 装配体模式下基准点线面 结束------
            case ViewerMode.Sketch:
                this.switchToSketchDefaultState();
                break;
            case ViewerMode.SketchDrawing:
                this.switchToDrawingState();
                break;
            case ViewerMode.SketchMoveEntities:
                this.switchToSketchMoveEntitiesState();
                break;
            case ViewerMode.SketchCopyEntities:
                this.switchToSketchCopyEntitiesState();
                break;
            case ViewerMode.SketchRotateEntities:
                this.switchToSketchRotateEntitiesState();
                break;
            case ViewerMode.SketchCircularPattern:
                this.switchToSketchCircularPatternState();
                break;
            case ViewerMode.SketchMirrorEntities:
                this.switchToSketchMirrorEntitiesState();
                break;
            case ViewerMode.SketchTrim:
                this.switchToTrimState();
                break;
            //------ 智能尺寸 开始------
            case ViewerMode.SketchDimension:
                /**
                 * 阶段 4 复用草图默认视觉态（hover/选中高亮语义一致）；
                 * 候选/锁定/冲突专属状态色属阶段 5 渲染交付。
                 */
                this.switchToSketchDefaultState();
                break;
            //------ 智能尺寸 结束------
            case ViewerMode.SketchOffset:
                this.switchToSketchDefaultState();
                break;
            default:
                throw new Error(`未支持的交互模式：${mode}`);
        }
        console.trace(`交互模式切换：${previous.mode} -> ${mode}`);
        this.visualStateHandler.clearStateMaterialCache();
        this.refreshVisualState();

        this.publishModeChange({
            previous,
            current: {...this.modeContext},
            timestamp: Date.now(),
        });
    }
    //------ 模式切换 结束------

    /**
     * 获取当前交互模式。
     * @returns 当前模式上下文。
     */
    public getMode(): InteractionModeContext {
        return {...this.modeContext};
    }

    /**
     * 注册模式变化监听器。
     * @param listener 监听器。
     */
    public onModeChange(listener: InteractionModeChangeListener): void {
        if (this.modeChangeSubscriptions.has(listener)) {
            return;
        }

        const subscription = this.modeChangeSubject.asObservable().subscribe(listener);
        this.modeChangeSubscriptions.set(listener, subscription);
    }

    /**
     * 注册一次性模式变化监听器。
     * @param listener 监听器。
     */
    public onceModeChange(listener: InteractionModeChangeListener): void {
        this.modeChangeSubject.pipe(take(1)).subscribe(listener);
    }

    /**
     * 取消注册模式变化监听器。
     * @param listener 监听器。
     */
    public offModeChange(listener: InteractionModeChangeListener): void {
        const subscription = this.modeChangeSubscriptions.get(listener);
        if (!subscription) {
            return;
        }

        subscription.unsubscribe();
        this.modeChangeSubscriptions.delete(listener);
    }

    /**
     * 释放资源。
     */
    public dispose(): void {
        this.stop();
        this.visualStateHandler.dispose();
        this.relationLabelState.dispose();
        this.modeChangeSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.modeChangeSubscriptions.clear();
        this.modeChangeSubject.complete();
    }

    /**
     * 同步外部主动修改后的对象材质快照。
     * @param object 外部已更新材质的对象。
     */
    public syncExternalMaterialSnapshot(object: THREE.Object3D): void {
        this.visualStateHandler.syncExternalMaterialSnapshot(object, this.createVisualStateContext());
    }

    /**
     * 同步指定对象的材质快照。
     * @param object 目标对象。
     * @param material 指定材质。
     */
    public syncMaterialSnapshot(object: THREE.Object3D, material: THREE.Material): void {
        this.visualStateHandler.syncMaterialSnapshot(object, material, this.createVisualStateContext());
    }

    /**
     * 主动请求当前交互视觉状态重算。
     * 说明：
     * 1. 该入口服务于“对象语义标记已变化，但未经过选择/悬浮事件”的场景；
     * 2. Convert Entities 的来源高亮、preview 标记都属于这类程序化视觉变更；
     * 3. 统一从这里触发，可避免宿主层直接依赖私有 `refreshVisualState()`。
     */
    public requestVisualRefresh(): void {
        if (!this.isStarted) {
            return;
        }

        this.refreshVisualState();
    }

    //------ 几何关系面板右键取消选择 开始------
    /**
     * 按对象 ID 列表同步当前选择状态。
     *
     * 设计说明：
     * 1. 几何关系面板“取消选择 / 取消全部选择”需要从 UI 侧主动写回选择中心；
     * 2. 宿主层直接调用该方法即可驱动 `EntitySelectionManager` 完成选择结算并派发事件；
     * 3. `InteractionStateManager` 继续只承担“状态协同入口”职责，不在宿主层散落选择改写逻辑。
     *
     * @param objectIds 目标对象 ID 列表。
     */
    public syncSelectionByObjectIds(objectIds: string[]): void {
        const selectionManager = this.entitySelectionManager ?? this.renderer.entitySelectionManager;
        if (!selectionManager) {
            return;
        }

        const normalizedObjectIds = Array.from(new Set(
            objectIds.filter((objectId) => typeof objectId === 'string' && objectId.length > 0),
        ));

        if (normalizedObjectIds.length <= 0) {
            selectionManager.clearSelectionProgrammatically();
            return;
        }

        const selectedObjects = normalizedObjectIds
            .map((objectId) => this.renderer.findSceneObjectById(objectId))
            .filter((object): object is THREE.Object3D => object !== null);

        if (selectedObjects.length <= 0) {
            selectionManager.clearSelectionProgrammatically();
            return;
        }

        selectionManager.selectEntitiesProgrammatically(selectedObjects);
    }
    //------ 几何关系面板右键取消选择 结束------

    //------ 基础视觉状态同步 开始------
    /**
     * 同步对象基础视觉状态，并立即刷新当前交互视觉结果。
     * 说明：
     * 1. 该方法用于对象默认视觉语义发生变化后的重算；
     * 2. 例如：普通线切换为构造线时，需要在保持 Selected / Hover 的同时切到对应基础线型体系；
     * 3. 刷新完成后，取消选中时会恢复到新的基础视觉状态。
     * @param object 目标对象。
     */
    public syncObjectBaseVisualState(object: THREE.Object3D): void {
        this.visualStateHandler.syncExternalMaterialSnapshot(object, this.createVisualStateContext());

        if (!this.isStarted) {
            return;
        }

        this.refreshVisualState();
    }
    //------ 基础视觉状态同步 结束------

    /**
     * 记录对象原始材质。
     * @param object 目标对象。
     */
    public snapshotOriginalMaterial(object: THREE.Mesh | THREE.Line | THREE.Sprite): void {
        this.visualStateHandler.snapshotOriginalMaterial(object);
    }

    /**
     * 查询对象原始材质快照。
     *
     * 设计说明：
     * 1. 提交类命令可通过该方法读取对象进入选中态前的材质；
     * 2. 该方法为只读查询，不触发视觉状态刷新；
     * 3. 未命中时返回 `null`，调用方应保留当前材质或采用其它兜底策略。
     *
     * @param objectId 对象 UUID。
     * @returns 原始材质快照；未命中时返回 `null`。
     */
    public getOriginalMaterialSnapshotByObjectId(objectId: string): MaterialSnapshot | null {
        return this.visualStateHandler.getOriginalMaterialSnapshotByObjectId(objectId);
    }

    //------ 草图对象状态外部控制 开始------
    /**
     * 显式设置草图对象交互状态。
     * 说明：
     * 1. 该方法用于快速点捕捉等“非选择管理器事件驱动”的临时高亮场景；
     * 2. 仅接受具备独立渲染材质的草图对象（点、线、圆弧、圆、椭圆、样条）；
     * 3. 传入 {@link GeometryInteractionState.Default} 时等价于恢复对象状态。
     * @param object 目标草图对象。
     * @param state 目标交互状态。
     * @throws 当对象不支持外部草图状态控制时抛出错误。
     */
    public setSketchObjectState(object: THREE.Object3D, state: GeometryInteractionState): void {
        if (!this.isSupportedSketchStateObject(object)) {
            throw new Error(`对象不支持外部草图状态控制：${object.uuid}`);
        }

        if (state === GeometryInteractionState.Default) {
            this.restoreSketchObjectState(object);
            return;
        }

        // 先记录对象原始材质，确保后续恢复时能回到正确基础态。
        this.snapshotOriginalMaterial(object);
        this.visualStateRuntime.manualStateHints.set(object.uuid, {
            object,
            state,
        });

        if (!this.isStarted) {
            return;
        }

        this.refreshVisualState();
    }

    /**
     * 恢复草图对象交互状态。
     * 说明：
     * 1. 移除由 {@link setSketchObjectState} 写入的外部状态提示；
     * 2. 刷新后对象会回到当前状态机计算出的默认/选中/悬浮态；
     * 3. 若对象当前仍被真正选中或移入，恢复后会继续显示对应状态。
     * @param object 目标草图对象。
     * @throws 当对象不支持外部草图状态恢复时抛出错误。
     */
    public restoreSketchObjectState(object: THREE.Object3D): void {
        if (!this.isSupportedSketchStateObject(object)) {
            throw new Error(`对象不支持外部草图状态恢复：${object.uuid}`);
        }

        this.visualStateRuntime.manualStateHints.delete(object.uuid);

        if (!this.isStarted) {
            return;
        }

        this.refreshVisualState();
    }

    /**
     * 设置草图点对象的手动可见性提示。
     * 说明：
     * 1. 该提示只影响交互态默认刷新时的可见性收口，不直接触发刷新；
     * 2. 点捕捉策略会先写入该提示，再写入 Hover 状态，避免同步渲染出现“先隐藏再显示”的中间态；
     * 3. 传入 false 时移除提示，对象会回到拓扑默认显隐规则。
     * @param object 目标草图点对象。
     * @param visible 是否保持可见。
     * @throws 当对象不是草图点时抛出错误。
     */
    public setSketchObjectVisibilityHint(object: THREE.Object3D, visible: boolean): void {
        if (!isSketchPointObject(object)) {
            throw new Error(`对象不支持外部草图可见性提示：${object.uuid}`);
        }

        if (visible) {
            this.visualStateRuntime.manualVisibilityHints.add(object.uuid);
            return;
        }

        this.visualStateRuntime.manualVisibilityHints.delete(object.uuid);
    }
    //------ 草图对象状态外部控制 结束------

    //------ 关系标签外部委托 开始------

    /**
     * 刷新指定对象上的关系标签。
     * @param object 目标草图对象。
     * @param visible 标签可见性。
     * @returns 实际关联标签的对象 ID 列表。
     */
    public refreshObjectRelationMarkers(object: THREE.Object3D, visible: boolean): string[] {
        return this.relationLabelState.refreshObjectMarkers(object, visible);
    }

    /**
     * 移除指定对象上某个约束对应的关系标签 CSS 对象。
     * @param object 目标草图对象。
     * @param constraintId 约束 ID。
     */
    public removeObjectConstraintMarker(object: THREE.Object3D, constraintId: number): void {
        this.relationLabelState.removeConstraintMarker(object, constraintId);
    }

    //------ 拖拽期间关系标签隐藏 开始------
    /**
     * 设置“拖拽期间关系标签隐藏”状态。
     * 说明：
     * 1. 开启后统一隐藏当前全部关系标签；
     * 2. 关闭后按当前交互状态重新计算并恢复标签显示；
     * 3. 仅影响关系标签显示，不影响约束数据与对象选中状态。
     * @param hidden 是否隐藏。
     */
    public setRelationMarkerHiddenDuringDrag(hidden: boolean): void {
        this.relationLabelState.setMarkerForceHidden(hidden);
        if (!hidden) {
            this.refreshVisualState();
        }
    }
    //------ 拖拽期间关系标签隐藏 结束------

    //------ 设计树程序化特征移入高亮 开始------
    /**
     * 设置当前程序化移入命中的特征结果体。
     *
     * 设计说明：
     * 1. 设计树移入特征节点时，`EntitySelectionManager` 会先解析 `FeatureResultMesh`；
     * 2. 这里统一保存“当前程序化特征 hover 真相”，由默认视觉状态在刷新时消费；
     * 3. 这样选择管理器只负责识别对象，不直接承担线面联动高亮逻辑。
     *
     * @param featureResultMesh 当前程序化移入命中的结果体；为空时清空。
     * @param refresh 是否立即刷新视觉状态。
     */
    public setProgrammaticHoveredFeatureResultMesh(
        featureResultMesh: FeatureResultMesh | null,
        refresh: boolean = true,
    ): void {
        if (this.programmaticHoveredFeatureResultMesh?.uuid === featureResultMesh?.uuid) {
            return;
        }
        this.programmaticHoveredFeatureResultMesh = featureResultMesh;
        if (refresh) {
            this.refreshVisualState();
        }
    }

    /**
     * 设置设计树特征节点 hover 的局部视觉范围。
     *
     * 设计说明：
     * 1. 合并实体中 feature 节点 hover 只能显示该 feature 自己的贡献面/边；
     * 2. 该状态不写入真实选择集合，避免影响拾取、测量、倒角、圆角等命令态；
     * 3. 传入 null 时只清理局部 hover，不改变用户当前主选对象。
     *
     * @param scope 局部视觉范围；为 null 时清空。
     * @param refresh 是否立即刷新视觉状态。
     */
    public setProgrammaticHoveredFeatureVisualScope(
        scope: FeatureVisualScope | null,
        refresh: boolean = true,
    ): void {
        const nextKey = this.resolveFeatureVisualScopeKey(scope);
        if (this.programmaticHoveredFeatureVisualScopeKey === nextKey) {
            return;
        }

        this.programmaticHoveredFeatureVisualScope = scope;
        this.programmaticHoveredFeatureVisualScopeKey = nextKey;
        if (refresh) {
            this.refreshVisualState();
        }
    }

    /**
     * 设置设计树特征节点选中的局部视觉范围。
     *
     * @param scope 局部视觉范围；为 null 时清空。
     * @param refresh 是否立即刷新视觉状态。
     */
    public setSelectedFeatureVisualScope(
        scope: FeatureVisualScope | null,
        refresh: boolean = true,
    ): void {
        const nextKey = this.resolveFeatureVisualScopeKey(scope);
        if (this.selectedFeatureVisualScopeKey === nextKey) {
            return;
        }

        this.selectedFeatureVisualScope = scope;
        this.selectedFeatureVisualScopeKey = nextKey;
        if (scope) {
            this.selectedObjects = [];
            this.selectedInteractionMeta = undefined;
        }
        if (refresh) {
            this.refreshVisualState();
        }
    }

    /**
     * 生成局部视觉范围的稳定比较键。
     *
     * @param scope 局部视觉范围。
     * @returns 稳定比较键。
     */
    private resolveFeatureVisualScopeKey(scope: FeatureVisualScope | null): string {
        if (!scope) {
            return '';
        }
        const meshKeys = scope.resultMeshes
            .map((mesh) => `${mesh.uuid}@${mesh.getTopologyVisualRevision()}`)
            .join('|');
        return `${scope.featureId}:${meshKeys}`;
    }

    //------ 设计树程序化特征移入高亮 结束------

    //------ 线性零部件阵列节点包围盒移入 开始------
    /**
     * 设置当前程序化多目标移入对象。
     *
     * 设计说明：
     * 1. 线性零部件阵列设计树分组没有单一几何对象可表达全部实例；
     * 2. 因此需要保存一组稳定的场景对象，由装配体视觉状态分别绘制包围盒；
     * 3. 该入口不参与真实射线拾取，只服务设计树这类程序化 hover。
     *
     * @param objects 当前程序化移入对象列表。
     * @param interactionMeta 交互语义元信息。
     * @param refresh 是否立即刷新视觉状态。
     */
    public setProgrammaticHoveredObjects(
        objects: THREE.Object3D[],
        interactionMeta?: SelectionInteractionMeta,
        refresh: boolean = true,
    ): void {
        const uniqueObjects = this.uniqueObjects(objects);
        const currentKey = this.programmaticHoveredObjects.map((object) => object.uuid).join('|');
        const nextKey = uniqueObjects.map((object) => object.uuid).join('|');
        if (currentKey === nextKey && this.programmaticHoveredInteractionMeta === interactionMeta) {
            return;
        }

        this.programmaticHoveredObjects = uniqueObjects;
        this.programmaticHoveredInteractionMeta = uniqueObjects.length > 0 ? interactionMeta : undefined;
        if (refresh) {
            this.refreshVisualState();
        }
    }
    //------ 线性零部件阵列节点包围盒移入 结束------


    //------ 关系标签外部委托 结束------

    /** 实体移入事件处理器。 */
    private readonly handleEntityHoverEnter = (payload: EntityHoverEnterEventPayload): void => {
        this.hoveredObject = payload.object;
        this.hoveredInteractionMeta = payload.interactionMeta;
        this.renderer.scheduleVisualUpdate(this, this.applyPendingVisualState);
    };

    /** 实体移出事件处理器。 */
    private readonly handleEntityHoverLeave = (payload: EntityHoverLeaveEventPayload): void => {
        if (!this.hoveredObject || this.hoveredObject.uuid !== payload.object.uuid) {
            return;
        }

        this.hoveredObject = null;
        this.hoveredInteractionMeta = undefined;
        this.renderer.scheduleVisualUpdate(this, this.applyPendingVisualState);
    };

    /** 实体选中事件处理器。 */
    private readonly handleEntitySelected = (payload: EntitySelectedEventPayload): void => {
        this.selectedFeatureVisualScope = null;
        this.selectedFeatureVisualScopeKey = '';
        this.selectedObjects = this.uniqueObjects(payload.selectedObjects);
        this.selectedInteractionMeta = payload.interactionMeta;
        this.refreshVisualState();
    };

    /** 实体框选事件处理器。 */
    private readonly handleEntityBoxSelected = (payload: EntityBoxSelectedEventPayload): void => {
        /**
         * 框选事件的 selectedObjects 仅表示“本次框选命中集合”。
         * 当前最终选中状态统一以 EntitySelected 事件为准，这里只触发视觉刷新。
         */
        void payload;
        this.refreshVisualState();
    };

    /**
     * 刷新视觉状态。
     */
    private refreshVisualState(): void {
        // 显式视觉刷新（例如选择提交、模式切换）已包含最终 Hover，取消此前待消费任务。
        this.renderer.cancelVisualUpdate(this);
        this.applyPendingVisualState();
    }

    /** 帧调度读取当前最终 Hover，不捕获已过期载荷；失败任务由调度器保留重试。 */
    private readonly applyPendingVisualState = (): void => {
        this.visualStateHandler.refreshVisualState(this.createVisualStateContext());

        if (this.renderer.measureController.isMeasureActive() && this.measureSelectedObjects.length > 0) {
            this.measureSelectionOverlay.applySelectedAppearance(this.measureSelectedObjects);
        } else {
            this.measureSelectionOverlay.clearSelectedAppearance();
        }

        /**
         * 交互状态刷新后必须登记重绘，由 Viewer 合并同帧请求。
         *
         * 说明：
         * 1. 悬停/选中/框选高亮属于事件驱动的材质切换，不能假设渲染循环一定持续运行；
         * 2. 若当前视口处于低频或按需渲染状态，仅更新材质而不触发重绘，用户会看到“高亮没有生效”；
         * 3. 在统一收口点请求重绘，可保证所有交互状态变更都拥有一致的可见性语义。
         */
        this.renderer.requestRender();
    };

    /**
     * 解析当前应参与底层视觉刷新的选中对象。
     *
     * 设计说明：
     * 1. 测量拾取启用时，图形区只表达测量选中（绿），底层命令选中（蓝）必须完全屏蔽；
     * 2. 右键菜单 Select 暂停测量拾取后，恢复底层选中视觉，便于临时执行普通选择；
     * 3. 数据层选中仍由 `EntitySelectionManager` 维护，此处仅收口视觉上下文。
     *
     * @returns 底层 TransitionState 应消费的选中对象列表。
     */
    private resolveVisualSelectedObjects(): THREE.Object3D[] {
        if (this.renderer.measureController.isPickEnabled()) {
            return [];
        }
        return [...this.selectedObjects];
    }

    /**
     * 创建视觉状态上下文。
     * @returns 视觉状态上下文。
     */
    public createVisualStateContext(): InteractionVisualStateContext {
        const visualSelectedObjects = this.resolveVisualSelectedObjects();
        const isMeasurePickEnabled = this.renderer.measureController.isPickEnabled();

        return {
            modeContext: {...this.modeContext},
            hoveredObject: this.hoveredObject,
            hoveredInteractionMeta: this.hoveredInteractionMeta,
            //------ 线性零部件阵列节点包围盒移入 开始------
            programmaticHoveredObjects: [...this.programmaticHoveredObjects],
            programmaticHoveredInteractionMeta: this.programmaticHoveredInteractionMeta,
            //------ 线性零部件阵列节点包围盒移入 结束------
            //------ 设计树程序化特征移入高亮 开始------
            programmaticHoveredFeatureResultMesh: this.programmaticHoveredFeatureResultMesh,
            programmaticHoveredFeatureVisualScope: isMeasurePickEnabled
                ? null
                : this.programmaticHoveredFeatureVisualScope,
            //------ 设计树程序化特征移入高亮 结束------
            selectedObjects: visualSelectedObjects,
            selectedInteractionMeta: visualSelectedObjects.length > 0
                ? this.selectedInteractionMeta
                : undefined,
            selectedFeatureVisualScope: isMeasurePickEnabled
                ? null
                : this.selectedFeatureVisualScope,
            commandSelectedTopologyProxyObjects:
                this.entitySelectionManager?.getCommandSelectedTopologyProxyObjects() ?? [],
            relationLabelState: this.relationLabelState,
            syncedMarkerObjectIdSet: this.syncedMarkerObjectIdSet,
            measureSelectedObjectIds: this.renderer.measureController.isMeasureActive()
                ? collectMeasureSelectionVisualObjectIds(this.measureSelectedObjects)
                : new Set<string>(),
        };
    }

    /**
     * 对对象数组去重。
     * @param objects 待去重对象数组。
     * @returns 去重后的对象数组。
     */
    private uniqueObjects(objects: THREE.Object3D[]): THREE.Object3D[] {
        const uniqueMap = new Map<string, THREE.Object3D>();

        objects.forEach((object) => {
            if (!uniqueMap.has(object.uuid)) {
                uniqueMap.set(object.uuid, object);
            }
        });

        return Array.from(uniqueMap.values());
    }

    /**
     * 判断对象是否支持外部草图状态控制。
     * @param object 待判断对象。
     * @returns 是否支持。
     */
    private isSupportedSketchStateObject(object: THREE.Object3D): object is THREE.Mesh | THREE.Line | THREE.Sprite {
        return isSketchPointObject(object)
            || isSketchLineObject(object)
            || isSketchArcObject(object)
            || isSketchCircleObject(object)
            || isSketchEllipseObject(object)
            || isSketchParabolaObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object);
    }

    /**
     * 派发模式变化事件。
     * @param payload 事件载荷。
     */
    private publishModeChange(payload: InteractionModeChangePayload): void {
        this.modeChangeSubject.next(payload);
    }

}
